import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Syringe, CheckCircle2, CalendarClock, Pencil, Download, FileText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { VaccineDriveForm } from "@/components/VaccineDriveForm";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function statusColor(s) {
  return s === "confirmed" ? "bg-primary" : s === "rescheduled" ? "bg-orange-500" : "bg-slate-400";
}

function csvCell(v) {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function vaccNames(p) {
  const list = p.vaccines && p.vaccines.length ? p.vaccines : (p.vaccine ? [p.vaccine] : []);
  return list.map((v) => ({ flu: "Flu", covid: "COVID", rsv: "RSV" }[v] || v)).join(", ");
}

function exportDrive(r) {
  if (!r) return;
  const lines = [];
  const kv = (k, v) => lines.push(`${csvCell(k)},${csvCell(v)}`);
  lines.push("Crystal Pharmacy — Vaccine Drive Request");
  kv("Status", r.status);
  kv("Source", r.source === "partner" ? "Health Partner" : "Public");
  kv("Requested on", (r.created_at || "").slice(0, 10));
  kv("Facility / Address", r.address);
  kv("Date", r.date);
  kv("Time", r.time);
  if (r.reschedule_note) kv("Reschedule note", r.reschedule_note);
  lines.push("");
  lines.push("Group,Requested,Flu,COVID,RSV");
  lines.push(["Staff", r.staff_count || 0, r.staff_flu || 0, r.staff_covid || 0, r.staff_rsv || 0].map(csvCell).join(","));
  lines.push(["Residents", r.residents_count || 0, r.residents_flu || 0, r.residents_covid || 0, r.residents_rsv || 0].map(csvCell).join(","));
  lines.push("");
  lines.push("Contacts");
  lines.push("First name,Last name,Phone,Email");
  (r.contacts || []).forEach((c) => lines.push([c.first_name, c.last_name, c.phone, c.email].map(csvCell).join(",")));
  if ((r.patients || []).length) {
    lines.push("");
    lines.push("Patient roster");
    lines.push("Full name,Date of birth,Vaccine,Insurance coverage,Insurance(s)");
    (r.patients || []).forEach((p) => {
      const ins = (p.insurances || []).map((x) => [x.provider, x.member_id, x.group_number, x.plan].filter(Boolean).join(" / ")).filter(Boolean).join("; ");
      const nm = (p.full_name || `${p.first_name || ""} ${p.last_name || ""}`).trim();
      const vacc = vaccNames(p);
      const cov = { covered: "Covered", not_covered: "Not covered" }[p.insurance_coverage] || "";
      lines.push([nm, p.date_of_birth || p.dob || "", vacc, cov, ins].map(csvCell).join(","));
    });
  }
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safe = (r.address || "drive").replace(/[^a-z0-9]+/gi, "-").slice(0, 40).replace(/^-|-$/g, "");
  a.href = url; a.download = `vaccine-drive-${safe || "request"}-${r.date || ""}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  toast.success("Export downloaded.");
}

function downloadCsv(text, filename) {
  const blob = new Blob(["\ufeff" + text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}

function exportAll(rows) {
  if (!rows || !rows.length) { toast.error("No drive requests to export."); return; }
  const headers = ["Requested On", "Status", "Source", "Date", "Time", "Address", "Contacts",
    "Staff Requested", "Staff Flu", "Staff COVID", "Staff RSV",
    "Residents Requested", "Residents Flu", "Residents COVID", "Residents RSV", "Roster Count", "Reschedule Note"];
  const lines = [headers.map(csvCell).join(",")];
  rows.forEach((r) => {
    const contacts = (r.contacts || []).map((c) => `${c.first_name} ${c.last_name} <${c.email}>${c.phone ? ` ${c.phone}` : ""}`).join("; ");
    lines.push([
      (r.created_at || "").slice(0, 10), r.status, r.source === "partner" ? "Health Partner" : "Public",
      r.date, r.time, r.address, contacts,
      r.staff_count || 0, r.staff_flu || 0, r.staff_covid || 0, r.staff_rsv || 0,
      r.residents_count || 0, r.residents_flu || 0, r.residents_covid || 0, r.residents_rsv || 0,
      (r.patients || []).length, r.reschedule_note || "",
    ].map(csvCell).join(","));
  });
  downloadCsv(lines.join("\n"), `vaccine-drives-all-${new Date().toISOString().slice(0, 10)}.csv`);
  toast.success(`Exported ${rows.length} drive request(s).`);
}

function exportDrivePDF(r) {
  if (!r) return;
  const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[m]));
  const contactsRows = (r.contacts || []).map((c) => `<tr><td>${esc(c.first_name)} ${esc(c.last_name)}</td><td>${esc(c.phone)}</td><td>${esc(c.email)}</td></tr>`).join("") || `<tr><td colspan="3">—</td></tr>`;
  const roster = (r.patients || []).length ? `<h3>Patient roster (${r.patients.length})</h3><table><thead><tr><th>Name</th><th>Date of birth</th><th>Vaccine</th><th>Coverage</th><th>Insurance(s)</th></tr></thead><tbody>${r.patients.map((p) => { const ins = (p.insurances || []).map((x) => [x.provider, x.member_id, x.group_number, x.plan].filter(Boolean).join(" / ")).filter(Boolean).join("; "); const nm = (p.full_name || `${p.first_name || ""} ${p.last_name || ""}`).trim(); const vacc = vaccNames(p) || "—"; const cov = { covered: "Covered", not_covered: "Not covered" }[p.insurance_coverage] || "—"; return `<tr><td>${esc(nm)}</td><td>${esc(p.date_of_birth || p.dob || "")}</td><td>${esc(vacc)}</td><td>${esc(cov)}</td><td>${esc(ins)}</td></tr>`; }).join("")}</tbody></table>` : "";
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Vaccine Drive — ${esc(r.address)}</title>
  <style>body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;margin:40px;}
  h1{color:#0d9488;margin:0 0 2px;font-size:24px;} .sub{color:#64748b;margin:0 0 20px;font-size:13px;letter-spacing:.08em;text-transform:uppercase;}
  h3{margin:22px 0 8px;border-bottom:2px solid #0d9488;padding-bottom:4px;color:#0d9488;font-size:15px;}
  table{border-collapse:collapse;width:100%;font-size:13px;margin-top:4px;} th,td{border:1px solid #cbd5e1;padding:6px 9px;text-align:left;}
  th{background:#f1f5f9;} .kv{font-size:14px;line-height:1.9;} .kv b{display:inline-block;width:170px;color:#334155;}
  .badge{display:inline-block;padding:2px 12px;border-radius:999px;background:#0d9488;color:#fff;font-size:12px;text-transform:capitalize;}</style></head>
  <body>
  <h1>Crystal Pharmacy</h1><p class="sub">Vaccine Drive Request</p>
  <div class="kv">
    <div><b>Status</b> <span class="badge">${esc(r.status)}</span></div>
    <div><b>Source</b> ${r.source === "partner" ? "Health Partner" : "Public"}</div>
    <div><b>Requested on</b> ${esc((r.created_at || "").slice(0, 10))}</div>
    <div><b>Facility / Address</b> ${esc(r.address)}</div>
    <div><b>Date</b> ${esc(r.date)}</div>
    <div><b>Time</b> ${esc(r.time)}</div>
    ${r.reschedule_note ? `<div><b>Reschedule note</b> ${esc(r.reschedule_note)}</div>` : ""}
  </div>
  <h3>Vaccination counts</h3>
  <table><thead><tr><th>Group</th><th>Requested</th><th>Flu</th><th>COVID</th><th>RSV</th></tr></thead>
  <tbody>
    <tr><td>Staff</td><td>${r.staff_count || 0}</td><td>${r.staff_flu || 0}</td><td>${r.staff_covid || 0}</td><td>${r.staff_rsv || 0}</td></tr>
    <tr><td>Residents</td><td>${r.residents_count || 0}</td><td>${r.residents_flu || 0}</td><td>${r.residents_covid || 0}</td><td>${r.residents_rsv || 0}</td></tr>
  </tbody></table>
  <h3>Contacts</h3>
  <table><thead><tr><th>Name</th><th>Phone</th><th>Email</th></tr></thead><tbody>${contactsRows}</tbody></table>
  ${roster}
  <script>window.onload=function(){setTimeout(function(){window.print();},250);};</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow pop-ups to print the PDF."); return; }
  w.document.write(html); w.document.close();
}

export default function AdminVaccineDrives() {
  const [rows, setRows] = useState([]);
  const [resched, setResched] = useState(null);
  const [form, setForm] = useState({ date: "", time: "", note: "" });
  const [edit, setEdit] = useState(null);

  useEffect(() => { load(); }, []);
  async function load() {
    try { const { data } = await api.get("/admin/vaccine-drive/requests"); setRows(data); } catch { /* */ }
  }

  async function confirm(id) {
    try { await api.patch(`/admin/vaccine-drive/requests/${id}/confirm`); toast.success("Confirmed — the contact(s) were emailed."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  function openResched(r) { setResched(r); setForm({ date: r.date, time: r.time, note: "" }); }
  async function submitResched(e) {
    e.preventDefault();
    try { await api.patch(`/admin/vaccine-drive/requests/${resched.id}/reschedule`, form); toast.success("Rescheduled — the contact(s) were emailed the new time."); setResched(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }
  async function saveEdit(payload) {
    try { await api.patch(`/admin/vaccine-drive/requests/${edit.id}`, payload); toast.success("Request updated."); setEdit(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  }

  return (
    <div className="space-y-6" data-testid="admin-vaccine-drives">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary"><Syringe className="h-6 w-6" /></div>
          <div>
            <h1 className="font-heading text-2xl font-bold">Vaccine Drives</h1>
            <p className="text-sm text-muted-foreground">Confirm or reschedule vaccine drive requests. Confirming emails the contact a HIPAA-compliant account link.</p>
          </div>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => exportAll(rows)} data-testid="vd-export-all"><Download className="mr-1 h-4 w-4" /> Export all (CSV)</Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Requested</TableHead><TableHead>Date / Time</TableHead><TableHead>Address</TableHead>
            <TableHead>Contacts</TableHead><TableHead>Counts</TableHead><TableHead>Source</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 ? <TableRow><TableCell colSpan={8} className="py-10 text-center text-muted-foreground">No vaccine drive requests yet.</TableCell></TableRow>
              : rows.map((r) => (
                <TableRow key={r.id} data-testid={`vd-row-${r.id}`}>
                  <TableCell className="text-xs text-muted-foreground">{(r.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell className="text-sm font-medium">{r.date}<br /><span className="text-muted-foreground">{r.time}</span></TableCell>
                  <TableCell className="text-sm">{r.address}</TableCell>
                  <TableCell className="text-sm">
                    {(r.contacts || []).map((c, i) => <div key={i}>{c.first_name} {c.last_name}<br /><span className="text-xs text-muted-foreground">{c.email}{c.phone ? ` · ${c.phone}` : ""}</span></div>)}
                  </TableCell>
                  <TableCell className="text-sm">Staff: {r.staff_count}<br /><span className="text-xs text-muted-foreground">Flu {r.staff_flu || 0} · COVID {r.staff_covid || 0} · RSV {r.staff_rsv || 0}</span><br />Residents: {r.residents_count}<br /><span className="text-xs text-muted-foreground">Flu {r.residents_flu || 0} · COVID {r.residents_covid || 0} · RSV {r.residents_rsv || 0}</span>{(r.patients || []).length ? <><br /><span className="text-xs text-muted-foreground">Roster: {(r.patients || []).length}</span></> : null}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{r.source === "partner" ? "Partner" : "Public"}</Badge></TableCell>
                  <TableCell><Badge className={statusColor(r.status)}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button size="sm" className="rounded-full" onClick={() => confirm(r.id)} data-testid={`vd-confirm-${r.id}`}><CheckCircle2 className="mr-1 h-3 w-3" /> Confirm</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openResched(r)} data-testid={`vd-resched-${r.id}`}><CalendarClock className="mr-1 h-3 w-3" /> Reschedule</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEdit(r)} data-testid={`vd-edit-${r.id}`}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportDrive(r)} data-testid={`vd-export-${r.id}`}><Download className="mr-1 h-3 w-3" /> CSV</Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => exportDrivePDF(r)} data-testid={`vd-pdf-${r.id}`}><FileText className="mr-1 h-3 w-3" /> PDF</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reschedule vaccine drive</DialogTitle>
            <DialogDescription>Propose a new date/time. The contact(s) will be emailed the update.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitResched} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Date</Label><Input type="date" data-testid="vd-resched-date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required /></div>
              <div className="space-y-1.5"><Label className="text-xs">Time</Label><Input type="time" data-testid="vd-resched-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Note (optional)</Label><Input data-testid="vd-resched-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Reason / message to the contact" /></div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="rounded-full" onClick={() => exportDrive(resched)} data-testid="vd-resched-export"><Download className="mr-1 h-4 w-4" /> CSV</Button>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => exportDrivePDF(resched)} data-testid="vd-resched-pdf"><FileText className="mr-1 h-4 w-4" /> PDF</Button>
              <Button type="submit" className="flex-1 rounded-full" data-testid="vd-resched-submit">Send new time</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Edit vaccine drive request</DialogTitle>
            <DialogDescription>Update any detail and save. Requests can be edited but not deleted.</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="mb-2 flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => exportDrive(edit)} data-testid="vd-edit-export"><Download className="mr-1 h-3.5 w-3.5" /> CSV</Button>
              <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => exportDrivePDF(edit)} data-testid="vd-edit-pdf"><FileText className="mr-1 h-3.5 w-3.5" /> PDF</Button>
            </div>
          )}
          {edit && (
            <VaccineDriveForm
              showStatus
              submitLabel="Save changes"
              onCancel={() => setEdit(null)}
              onSubmit={saveEdit}
              initial={{
                address: edit.address || "", date: edit.date || "", time: edit.time || "",
                staff_count: edit.staff_count ?? "", residents_count: edit.residents_count ?? "",
                staff_flu: edit.staff_flu ?? "", staff_covid: edit.staff_covid ?? "", staff_rsv: edit.staff_rsv ?? "",
                residents_flu: edit.residents_flu ?? "", residents_covid: edit.residents_covid ?? "", residents_rsv: edit.residents_rsv ?? "",
                status: edit.status || "pending",
                contacts: edit.contacts?.length ? edit.contacts : [{ first_name: "", last_name: "", phone: "", email: "" }],
                patients: (edit.patients || []).map((p) => ({ ...p, insurances: p.insurances?.length ? p.insurances : [{ provider: "", member_id: "", group_number: "", plan: "" }] })),
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
